import { useQuery, useInfiniteQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { ItemApiClient, DemotedItemApiClient } from 'api/item';
import { ItemModel, ListingModel } from 'models';
import { JSONObject } from 'models/Api/types';
import { ItemActiveMarketData } from 'models/Item/types';

export const useItem = (id: string, options?: Omit<UseQueryOptions<ItemModel>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: ['items', id],
    queryFn: () => ItemApiClient.getItemById(id),
    enabled: !!id,
    ...options,
  });
};

export const useItemMarket = (id: string, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: ['items', id, 'market'],
    queryFn: () => ItemApiClient.getItemMarket(id),
    enabled: !!id,
    ...options,
  });
};

export const useItemActiveMarket = (id: string, options?: Omit<UseQueryOptions<ItemActiveMarketData>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: ['items', id, 'active-market'],
    queryFn: () => ItemApiClient.getItemActiveMarket(id),
    enabled: !!id,
    ...options,
  });
};

export const useItems = (page = 1) => {
  return useQuery({
    queryKey: ['items', { page }],
    queryFn: () => ItemApiClient.getAllItems(page),
  });
};

export const useInfiniteItems = () => {
  return useInfiniteQuery({
    queryKey: ['items', 'infinite'],
    queryFn: ({ pageParam = 1 }) => ItemApiClient.getAllItems(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.currentPage < lastPage.totalPages) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
  });
}; 

export const useItemSuggestions = (id: string, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: ['itemSuggestions', id],
    queryFn: () => ItemApiClient.getItemSuggestions(id),
    enabled: !!id,
    ...options,
  });
};

export const useItemListings = (id: string, options?: Omit<UseQueryOptions<ListingModel[]>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: ['itemListings', id],
    queryFn: () => ItemApiClient.getItemListings(id),
    enabled: !!id,
    ...options,
  });
};

export const useDemotedItem = (id: string, options?: Omit<UseQueryOptions<ItemModel>, 'queryKey' | 'queryFn'>) => {
  return useQuery<ItemModel>({
    queryKey: ['demoted-items', id],
    queryFn: () => DemotedItemApiClient.getDemotedItemById(id),
    enabled: !!id,
    ...options,
  });
};

export const useDemotedItemSearch = (query: string, page: number = 1) => {
  return useQuery({
    queryKey: ['demoted-items-search', query, page],
    queryFn: () => DemotedItemApiClient.searchDemotedItemsByQuery(query, page),
    enabled: !!query
  });
};

export const useUpdateItem = (id: string) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (data: JSONObject) => ItemApiClient.updateItem(id, data),
    onSuccess: () => {
      // Refetch the item to get fresh data
      queryClient.invalidateQueries({ queryKey: ['items', id] });
      toast({
        title: 'Item updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update item',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
};

export const useDemoteItem = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<string, Error, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => ItemApiClient.demoteItem(id, reason),
    onSuccess: (newItemId, variables) => {
      // Invalidate old item and fetch new demoted item
      queryClient.invalidateQueries({ queryKey: ['items', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['demoted-items', newItemId] });
      toast({
        title: 'Item demoted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to demote item',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
};

export const usePromoteItem = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<string, Error, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => DemotedItemApiClient.promoteItem(id, reason),
    onSuccess: (newItemId, variables) => {
      // Invalidate old demoted item and fetch new promoted item
      queryClient.invalidateQueries({ queryKey: ['demoted-items', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['items', newItemId] });
      toast({
        title: 'Item promoted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to promote item',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
};

export const useCreateItemFromListing = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<string, Error, string>({
    mutationFn: (listingId: string) => ItemApiClient.createItemFromListing(listingId),
    onSuccess: (_newItemId, listingId) => {
      queryClient.invalidateQueries({ queryKey: ['listings', listingId] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      toast({
        title: 'Item created from listing successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create item from listing',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
};

export const useRefreshItemMarket = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => ItemApiClient.refreshItemMarket(id),
    onSuccess: (_data, itemId) => {
      queryClient.invalidateQueries({ queryKey: ['items', itemId, 'market'] });
      queryClient.invalidateQueries({ queryKey: ['items', itemId, 'active-market'] });
      toast({
        title: 'Market refresh requested',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to refresh market data',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
};

export const useEnhanceItem = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => ItemApiClient.enhanceItem(id),
    onSuccess: (_data, itemId) => {
      queryClient.invalidateQueries({ queryKey: ['items', itemId] });
      toast({
        title: 'Enhancement requested',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to enhance item',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
};

export const useRemoveItemBackground = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => ItemApiClient.removeItemBackground(id),
    onSuccess: (_data, itemId) => {
      queryClient.invalidateQueries({ queryKey: ['items', itemId] });
      toast({
        title: 'Background removal requested',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to remove background',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
};

export const useMigrateItemImage = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<void, Error, { id: string; imageUrl: string }>({
    mutationFn: ({ id, imageUrl }) => ItemApiClient.migrateItemImage(id, imageUrl),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['items', variables.id] });
      toast({
        title: 'Image migrated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to migrate image',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
};
